import { useTranslation } from 'react-i18next';
import { Field } from 'react-final-form';
import { TextInput } from '@utriper/core-ui';
import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, Button, Grid2, IconButton } from '@mui/material';

const QuantityRangeFields = ({ fields, lastToEmpty, canAdd = false, canRemove = false }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`quantity_range_fields.${key}`);
    const sanitizeNumberString = (str) => {
        if (str === undefined || str === null) return '';
        const cleaned = String(str).replace(/[^0-9]/g, '');
        return cleaned === '' ? '' : String(parseInt(cleaned, 10));
    };

    return (
        <Box width="100%">
            <Box sx={{ maxHeight: 320, overflowY: 'auto', width: '100%' }}>
                {fields.map((name, index) => {
                    return (
                        <Grid2 container spacing={1} alignItems="center" key={name} my={2}>
                            {/* Quantity From */}
                            <Grid2 size={canRemove ? 5.5 : 6}>
                                <Field name={`${name}.quantityFrom`}>
                                    {({ input, meta }) => (
                                        <TextInput
                                            label={translate('from')}
                                            meta={meta}
                                            value={input?.value}
                                            onChange={(event) => {
                                                const cleaned = sanitizeNumberString(event?.target?.value);
                                                event.target.value = cleaned;
                                                input.onChange(cleaned === '' ? '' : Number(cleaned));
                                            }}
                                            input={{
                                                fullWidth: true,
                                                type: 'text',
                                                inputMode: 'numeric',
                                                slotProps: {
                                                    inputProps: { inputMode: 'numeric', pattern: '[0-9]*', min: 1 },
                                                },
                                                error: meta?.touched && Boolean(meta?.error),
                                                helperText: meta?.touched && meta?.error ? meta.error : '',
                                            }}
                                        />
                                    )}
                                </Field>
                            </Grid2>

                            {/* Quantity To */}
                            <Grid2 size={canRemove ? 5.5 : 6}>
                                <Field name={`${name}.quantityTo`}>
                                    {({ input, meta }) => (
                                        <TextInput
                                            label={translate('to')}
                                            meta={meta}
                                            value={input?.value}
                                            onChange={(event) => {
                                                const raw = event?.target?.value;
                                                if (raw === '') {
                                                    input.onChange('');
                                                    return;
                                                }
                                                const cleaned = sanitizeNumberString(raw);
                                                event.target.value = cleaned;
                                                input.onChange(cleaned === '' ? '' : Number(cleaned));
                                            }}
                                            input={{
                                                fullWidth: true,
                                                type: 'text',
                                                inputMode: 'numeric',
                                                slotProps: {
                                                    inputProps: { inputMode: 'numeric', pattern: '[0-9]*', min: 1 },
                                                },
                                                error: meta?.touched && Boolean(meta?.error),
                                                helperText: meta?.touched && meta?.error ? meta.error : '',
                                            }}
                                        />
                                    )}
                                </Field>
                            </Grid2>

                            {/* Delete Button */}
                            {index !== 0 && canRemove && (
                                <Grid2 size={1} display="flex" justifyContent="flex-end">
                                    <IconButton aria-label="delete" disabled={fields?.length === 1} onClick={() => fields.remove(index)} size="small" color="error">
                                        <DeleteOutline />
                                    </IconButton>
                                </Grid2>
                            )}
                        </Grid2>
                    );
                })}
            </Box>

            {canAdd && (
                <Box display="flex" justifyContent="flex-start" alignItems="center" mt={2}>
                    <Button
                        sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }}
                        variant="text"
                        startIcon={<Add />}
                        onClick={() => {
                            const last = fields.value && fields.value[fields.length - 1];
                            let nextFrom = 1;
                            if (last) {
                                const lastTo = last.quantityTo === '' || last.quantityTo === undefined ? undefined : Number(last.quantityTo);
                                const lastFrom = last.quantityFrom === '' || last.quantityFrom === undefined ? undefined : Number(last.quantityFrom);
                                if (lastTo !== undefined) nextFrom = lastTo + 1;
                                else if (lastFrom !== undefined) nextFrom = lastFrom + 1;
                            }
                            fields.push({ quantityFrom: nextFrom, quantityTo: '' });
                        }}
                        disabled={lastToEmpty === true}
                    >
                        {translate('add_range')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default QuantityRangeFields;
